/**
 * API functions for video generation and management
 */
import { fetcher } from '@/config/api';

export interface VideoMetadata {
  id: string;
  prompt: string;
  url: string;
  local_path?: string;
  created_date: string;
  height: number;
  width: number;
  n_seconds: number;
  n_variants: number;
  job_id?: string;
}

export interface GenerateVideoRequest {
  prompt: string;
  height?: number;
  width?: number;
  n_seconds?: number;
  n_variants?: number;
}

export interface GenerateVideoResponse {
  success: boolean;
  url?: string;
  local_path?: string;
  job_id?: string;
  error?: string;
  duration?: number;
  dimensions?: string;
}

export interface VideoJobStatusResponse {
  success: boolean;
  status?: string;
  url?: string;
  error?: string;
  job_data?: any;
}

/**
 * Generate a video based on a text prompt
 */
export async function generateVideo(request: GenerateVideoRequest): Promise<GenerateVideoResponse> {
  try {
    return await fetcher<GenerateVideoResponse>('/api/videos/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  } catch (error) {
    console.error('Error generating video:', error);
    throw error;
  }
}

/**
 * Check the status of a video generation job
 */
export async function getVideoJobStatus(jobId: string): Promise<VideoJobStatusResponse> {
  try {
    return await fetcher<VideoJobStatusResponse>(`/api/videos/job/${jobId}/status`);
  } catch (error) {
    console.error('Error checking video job status:', error);
    throw error;
  }
}

/**
 * Get video generation history for the current user
 */
export async function getVideoHistory(limit: number = 20): Promise<VideoMetadata[]> {
  try {
    return await fetcher<VideoMetadata[]>(`/api/videos/history?limit=${limit}`);
  } catch (error) {
    console.error('Error fetching video history:', error);
    throw error;
  }
}

/**
 * Delete a video from history
 */
export async function deleteVideo(videoId: string): Promise<void> {
  try {
    await fetcher<void>(`/api/videos/${videoId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
}
